import React from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Checkbox, Form, Grid, Input } from 'semantic-ui-react'
import { CheckClimaEl } from '../../../style'
import { FormFields } from '~/components/FormFields'
import { splitEvery } from '~/utils/helpers'
import InfoPopup from '~/components/InfoPopup'
import RecycledManure from './RecycledManure'
import { ClimateCheckSelectType } from '~/api/services/climaCheck'
import LinkPopup from '../LinkPopup'

interface Props {
  options: any[]
  manureFermentations: ClimateCheckSelectType[]
}

const EightStep: React.FC<Props> = ({ options, manureFermentations }) => {
  const { t } = useTranslation()
  const { control, getValues, clearErrors } = useFormContext()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'calvesManureStorageTypes',
    keyName: 'key'
  })

  const watchCalvesHousingTypes = useWatch({ control, name: 'calvesHousingTypes' })
  
  const displayManureGrid = watchCalvesHousingTypes.find(housingType => housingType?.produceSlurryManure === 1)

  function handleChecked(field, checked) {
    if (checked) {
      append({
        ...field,
        percent: ''
      })
      return
    }
    const index = fields.findIndex(fieldManure => fieldManure['id'] === field.id)
    if (index >= 0) {
      remove(index)
    }
  }

  function checkPercentage() {
    const manureTypes = getValues('calvesManureStorageTypes')
    return manureTypes.reduce((previousValue, currentValue) => {
      return previousValue + parseFloat(currentValue?.percent?.replace(',', '.'))
    }, 0) != 100
  }

  const optionsSplit = splitEvery(options, 4)

  return <Form.Field>
    {displayManureGrid && <>
      <label style={{ fontSize: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>{t('Type of manure storage')}</div>
          <InfoPopup
            content={t('Please select all types of manure storage. Then distribute the percentage share among the selected options. In total these must add up to 100%.')}
            styleIcon={{ fontSize: '1.2em' }}
            position={'top center'}
        />
        </div>
      </label>
      <CheckClimaEl.StyledDivider />
      <Grid columns={2} style={{ marginTop: '1em' }}>
        <Controller
          name={'calvesManureStorageTypes'}
          control={control}
          rules={{
            validate: () => !checkPercentage()
          }}
          render={({ fieldState }) => {
            const error = fieldState.error ? checkPercentage() : false
            return <>
              {optionsSplit.map((optionSplit, index) => {
                return <Grid.Column key={`manureColumn-${index}`}>
                  <Grid columns={2}>
                    {optionSplit.map((option, index) => {
                      const fieldIndex = fields.findIndex(field => field['id'] === option.id)
                      return <Grid.Row
                        key={option.id}
                        style={{ alignItems: 'center', padding: '0.6rem 0rem' }}
                    >
                        <Grid.Column>
                          <Checkbox
                            label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <div>{option.label}</div>
                              {option?.infoPopup && <InfoPopup
                                content={option?.infoPopup}
                                styleIcon={{ fontSize: '1.2em' }}
                                position={'top center'}
                              />}
                              {option?.linkPopup && <LinkPopup
                                content={option?.linkPopup}
                              />}
                            </label>}
                            checked={Boolean(fields.find(manureField => manureField['id'] === option.id))}
                            onChange={(e, data) => handleChecked(option, data.checked)}
                        />
                        </Grid.Column>
                        <Grid.Column>
                          {fieldIndex >= 0 ? <FormFields.Input
                            control={control}
                            allowDecimals
                            decimalScale={2}
                            styleLabel={{ marginBottom: 0 }}
                            style={{ width: 200 }}
                            onBlur={() => {
                              if (!checkPercentage()) {
                                clearErrors('cowsManureStorageTypes')
                              }
                            }}
                            error={error}
                            typeInput={'controled'}
                            suffix={' %'}
                            noErrorMessage
                            withoutAsterisk
                            rules={{
                              required: true
                            }}
                            name={`calvesManureStorageTypes[${fieldIndex}].percent`}
                            placeholder={t('in %')}
                        /> : <Input
                          style={{ width: 200 }}
                          disabled
                          placeholder={t('in %')}
                        />
                        }
                        </Grid.Column>
                      </Grid.Row>
                    })}
                  </Grid>
                </Grid.Column>
              })}

            </>
          }}
      />
      </Grid>
    </>}
    <RecycledManure
      manureFermentations={manureFermentations}
    />
  </Form.Field>
}
export default EightStep